import { usersApi } from "../api";

/**
 * Determines whether the currently signed-in user has left the waitlist -
 * i.e. their `waitlist_status` is explicitly `null` (present but not on the
 * waitlist) or `"admitted"`. Such users should always see the normal iMessage
 * CTA, regardless of the `iris-cta-waitlist-enabled` flag.
 *
 * Returns `false` (i.e. don't force the normal CTA) for:
 *   - visitors with no Cognito session (the flag drives behavior for them),
 *   - signed-in users still `"waiting"` on the waitlist,
 *   - responses where `waitlist_status` is ABSENT entirely. A deployed api2
 *     always serializes the field (Pydantic emits `None` as `null`), so a
 *     missing field means we're talking to an older api2 that predates
 *     `waitlist_status`. We can't tell if they're still waiting, so stay
 *     conservative and keep them on the waitlist rather than defaulting to
 *     "admitted".
 *   - any error path (conservative: keep flag-driven waitlist gating rather
 *     than accidentally routing a waiting user to iMessage).
 *
 * Safe to call during SSR/prerender (returns `false` when `window` is absent).
 */
export async function isCurrentUserOffWaitlist(): Promise<boolean> {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    const { getCurrentUser } = await import("aws-amplify/auth");
    const user = await getCurrentUser();

    const result = await usersApi.getUser(user.userId);
    if (!result.success) {
      return false;
    }

    const status = result.data.waitlist_status;
    // Field absent (older api2 without waitlist_status) - can't determine, so
    // keep them on the waitlist instead of defaulting to admitted.
    if (status === undefined) {
      return false;
    }
    return status === null || status === "admitted";
  } catch {
    // No session (getCurrentUser throws when unauthenticated) or a transient
    // failure - fall back to flag-driven behavior.
    return false;
  }
}
